import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { UseMutateFunction } from "@tanstack/react-query";
import { colors } from "@zapalacorp/tokens";
import { AxiosResponse } from "axios";
import { BoxTick } from "iconsax-react";
import { Trans, useTranslation } from "react-i18next";

import ConfirmationModal from "../../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../../components/Form/Button";
import { JobStatus as OrderStatus } from "../../../../../util/enums";
import { capitalizeFirstCharacter } from "../../../../../util/Format";
import { ConfirmationModalWarningBody, ModalTextDescriptionInfo } from "./styles";

export interface LoadOrderConfirmationModalHandles {
  open: (id: number, status: OrderStatus, previousStatus?: OrderStatus) => void;
  close: () => void;
}
interface LoadOrderConfirmationModalProps {
  closeModal?: () => void;
  onSubmit: UseMutateFunction<
    AxiosResponse<boolean, any>,
    unknown,
    {
      id: number;
      status: OrderStatus;
    },
    unknown
  >;
  loadingAction?: boolean;
}

const ChangeOrderStatusConfirmationModal: ForwardRefRenderFunction<
  LoadOrderConfirmationModalHandles,
  LoadOrderConfirmationModalProps
> = (props, ref) => {
  const { t: translate } = useTranslation();
  const [status, setStatus] = useState<OrderStatus>();
  const [previousStatus, setPreviousStatus] = useState<OrderStatus>();
  const [id, setId] = useState<number>();
  const { closeModal, loadingAction, onSubmit } = props;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const open = useCallback((id: number, status: OrderStatus, previousStatus?: OrderStatus) => {
    setOpenModal(true);
    setId(id);
    setStatus(status);
    setPreviousStatus(previousStatus);
  }, []);
  const close = useCallback(() => {
    setOpenModal(false);
    setId(undefined);
    setStatus(undefined);
    setPreviousStatus(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const propsByStatus = useMemo(() => {
    switch (status) {
      case OrderStatus.LOADED:
      default:
        return {
          title: translate("loadOrder"),
          description: translate("areYouSureYouWantToLoadThisOrder"),
          submitText: translate("yesLoad"),
          icon: <BoxTick color={colors.success500} />,
        };
    }
  }, [status]);

  const cancelText = useMemo(() => {
    if (previousStatus) {
      return (
        <Trans
          i18nKey="noKeepItAsStatus"
          values={{ status: previousStatus?.toLowerCase() ?? "" }}
        />
      );
    }

    return translate("noKeepItAsItIs");
  }, [previousStatus]);

  return (
    <ConfirmationModal
      width="520px !important"
      open={openModal}
      closeModal={() => {
        if (closeModal) closeModal();
        close();
      }}
      type="success"
      title={propsByStatus.title}
      leftIconTitle={propsByStatus.icon}
      loading={loadingAction}
      cancelDisabled={loadingAction}
      customColorCancel={Color.TEXT_INFO}
      customColorSave={Color.SUCCESS}
      textCancel={cancelText}
      textSubmit={propsByStatus.submitText}
      submit={() => id && status && onSubmit({ id, status })}
    >
      <ConfirmationModalWarningBody data-cy="confirmationModalWarningBody">
        <ModalTextDescriptionInfo>{propsByStatus.description}</ModalTextDescriptionInfo>
        <ModalTextDescriptionInfo color={colors.success600}>
          <Trans
            i18nKey="changeOrderStatusModalDescription"
            values={{ status: capitalizeFirstCharacter(status ?? "") }}
          />
        </ModalTextDescriptionInfo>
      </ConfirmationModalWarningBody>
    </ConfirmationModal>
  );
};

export default forwardRef(ChangeOrderStatusConfirmationModal);
